import { FetchableResourceStatus } from '~/_shared/types/fetchableResource/fetchableResource';
import { isResourceLoaded } from '~/_shared/types/fetchableResource/fetchableResource.helpers';
import { copy } from '~/_shared/utils/collections/collections';
import type { StatsAction } from './stats.action';
import {
  addBoundaryStatsItems, requestBoundaryStatsItemsByIdIfExist, setHighlightStatsBoundaryGroup, updateBoundaryStatsItems,
} from './stats.actionCreators';
import {
  createEmptyBoundaryStatItemDemographic, createEmptyBoundaryStatItemLocationCount,
  createEmptyBoundaryStatItemSpreadsheetColumn,
} from './stats.factory';
import {
  findBoundaryGroupStatItemDemographic, findBoundaryGroupStatItemLocationCount,
  findBoundaryGroupStatItemSpreadsheetColumn, isBoundaryStatItemDemographicMetricData,
  isBoundaryStatItemLocationCountMetricData, isBoundaryStatItemSpreadsheetColumnMetricData,
} from './stats.helpers';
import type { StatsState } from './stats.state';

const initialState: StatsState = {
  boundaryGroupStats: new Map(),
  highlightStatsBoundaryGroup: null,
};

export const statsReducer = (
  state = initialState,
  action: StatsAction
): StatsState => {
  if (addBoundaryStatsItems.match(action)) {

    return action.payload.newItems.reduce((newState, item) => {
      let newItem = null;

      if (isBoundaryStatItemSpreadsheetColumnMetricData(item)) {
        const existing = findBoundaryGroupStatItemSpreadsheetColumn(newState.boundaryGroupStats, item);
        if (!existing) {
          newItem = createEmptyBoundaryStatItemSpreadsheetColumn(item);
        }
      }

      if (isBoundaryStatItemLocationCountMetricData(item)) {
        const existing = findBoundaryGroupStatItemLocationCount(newState.boundaryGroupStats, item);
        if (!existing) {
          newItem = createEmptyBoundaryStatItemLocationCount(item);
        }
      }

      if (isBoundaryStatItemDemographicMetricData(item)) {
        const existing = findBoundaryGroupStatItemDemographic(newState.boundaryGroupStats, item);
        if (!existing) {
          newItem = createEmptyBoundaryStatItemDemographic(item);
        }
      }

      if (newItem) {
        const newStatsItemForBoundaryGroup = copy.andReplace(
          newState.boundaryGroupStats.get(newItem.boundaryGroupId) || new Map(),
          newItem.id,
          newItem
        );

        return {
          ...newState,
          boundaryGroupStats: new Map(newState.boundaryGroupStats).set(
            newItem.boundaryGroupId,
            newStatsItemForBoundaryGroup
          ),
        };
      }
      return newState;

    }, state);
  }

  if (updateBoundaryStatsItems.match(action)) {
    return action.payload.items.reduce((newState, item) => {
      const currentBoundaryGroupStats = newState.boundaryGroupStats.get(item.boundaryGroupId);
      const currentItemById = currentBoundaryGroupStats?.get(item.id);

      if (currentBoundaryGroupStats && currentItemById) {
        const updatedBoundaryGroupStats = copy.andReplace(
          currentBoundaryGroupStats,
          item.id,
          item,
        );

        return {
          ...newState,
          boundaryGroupStats: new Map(newState.boundaryGroupStats).set(
            item.boundaryGroupId,
            updatedBoundaryGroupStats
          ),
        };
      }

      return newState;
    }, state);
  }

  if (requestBoundaryStatsItemsByIdIfExist.match(action)) {
    const updatedBoundaryGroupStats = new Map(
      Array.from(state.boundaryGroupStats.entries()).map(([boundaryGroupId, statsMap]) => {
        const updatedStatsMap = new Map(
          Array.from(statsMap.entries()).map(([itemId, statsItem]) => {
            if (
              isResourceLoaded(statsItem.data) &&
              action.payload.ids.includes(statsItem.data.value.serverId)
            ) {
              return [
                itemId,
                {
                  ...statsItem,
                  data: {
                    status: FetchableResourceStatus.Requested,
                  },
                },
              ];
            }
            return [itemId, statsItem];
          })
        );
        return [boundaryGroupId, updatedStatsMap];
      })
    );

    return {
      ...state,
      boundaryGroupStats: updatedBoundaryGroupStats,
    };
  }

  if (setHighlightStatsBoundaryGroup.match(action)) {
    return {
      ...state,
      highlightStatsBoundaryGroup: action.payload.boundaryGroupId,
    };
  }

  return state;
};
